import { hijriMonthId } from "../utils/hijri";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import ArabicText from "../components/shared/ArabicText";
import { getRandomAyah } from "../services/quranService";
import {
  BookOpen,
  Bookmark,
  Brain,
  Target,
  HandHeart,
  Flame,
  ChevronRight,
  Play,
  CheckCircle2,
  Circle,
  Clock,
  MapPin,
  Navigation,
  Sunrise,
  Sun,
  Sunset,
  Moon,
  Share2,
  Sparkles,
  BarChart3
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip
} from "recharts";

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { language, translation, dashboardPrefs, favoriteShortcuts } = useLanguage();
  const isEnglish = language === "en";
  const [dailyVerse, setDailyVerse] = useState(null);
  const [streak, setStreak] = useState(0);
  const [habits, setHabits] = useState([]);
  const [todayLogs, setTodayLogs] = useState([]);
  const [lastRead, setLastRead] = useState(null);
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [cityName, setCityName] = useState("");
  const [nextPrayer, setNextPrayer] = useState(null);
  const [countdownText, setCountdownText] = useState("");
  const [isTimeNow, setIsTimeNow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [sharingProgress, setSharingProgress] = useState(false);
  const [sharingQuote, setSharingQuote] = useState(false);
  const homeLoadRef = useRef({ inFlight: false, lastAt: 0 });
  const homeCacheRef = useRef({ data: null, fetchedAt: 0 });

  const quotes = [
    {
      id: "Sesungguhnya bersama kesulitan ada kemudahan.",
      en: "Indeed, with hardship comes ease.",
      source: "QS. Al-Insyirah: 6"
    },
    {
      id: "Janganlah kamu berputus asa dari rahmat Allah.",
      en: "Do not despair of the mercy of Allah.",
      source: "QS. Az-Zumar: 53"
    },
    {
      id: "Allah tidak membebani seseorang melainkan sesuai kesanggupannya.",
      en: "Allah does not burden a soul beyond its capacity.",
      source: "QS. Al-Baqarah: 286"
    },
    {
      id: "Ingatlah, hanya dengan mengingat Allah hati menjadi tenang.",
      en: "Verily, in the remembrance of Allah do hearts find rest.",
      source: "QS. Ar-Ra'd: 28"
    }
  ];

  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if today is Friday
  const isFriday = new Date().getDay() === 5;

  // Determine if should show Friday prayer (Jumat)
  // Default to male (show Jumat) if gender is not set
  const shouldShowFridayPrayer = isFriday && (!user?.gender || user?.gender === "male");

  // Prayer names mapping
  const prayerNames = {
    Fajr: { id: "Subuh", en: "Fajr", icon: Sunrise },
    Dhuhr: {
      id: shouldShowFridayPrayer ? "Jumat" : "Zuhur",
      en: shouldShowFridayPrayer ? "Friday" : "Dhuhr",
      icon: Sun
    },
    Asr: { id: "Ashar", en: "Asr", icon: Clock },
    Maghrib: { id: "Maghrib", en: "Maghrib", icon: Sunset },
    Isha: { id: "Isya", en: "Isha", icon: Moon },
  };

  useEffect(() => {
    loadHomeData();
  }, []);

  // Auto-refresh when tab becomes visible (handles day change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadHomeData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const applyHomeData = (payload) => {
    setDailyVerse(payload.dailyVerse);
    setStreak(payload.streak);
    setHabits(payload.habits);
    setTodayLogs(payload.todayLogs);
    setLastRead(payload.lastRead);
  };

  const fetchWithTimeout = (url, timeoutMs = 8000) => {
    if (typeof AbortController === "undefined") {
      return fetch(url);
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timeoutId));
  };

  const loadHomeData = async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force) {
      if (homeLoadRef.current.inFlight) return;
      if (now - homeLoadRef.current.lastAt < 5000) return;
      if (homeCacheRef.current.data && now - homeCacheRef.current.fetchedAt < 30000) {
        applyHomeData(homeCacheRef.current.data);
        setLoading(false);
        return;
      }
    }
    homeLoadRef.current.inFlight = true;
    homeLoadRef.current.lastAt = now;
    setLoading(true);

    const fetchLocationAndPrayerTimes = async () => {
      if (!navigator.geolocation) return;
      try {
        const position = await Promise.race([
          new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            });
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 8000)),
        ]);

        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        try {
          const lang = isEnglish ? "en" : "id";
          let response = await fetchWithTimeout(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${location.latitude}&longitude=${location.longitude}&localityLanguage=${lang}`
          );
          if (!response.ok) {
            response = await fetchWithTimeout(
              `https://geocode.maps.co/reverse?lat=${location.latitude}&lon=${location.longitude}`
            );
          }
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          const city =
            data.city ||
            data.locality ||
            data.principalSubdivision ||
            data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.suburb ||
            data.address?.county ||
            data.address?.state ||
            "";
          setCityName(city);
        } catch (error) {
          console.warn("Could not fetch city name from API (this is normal)");
          setCityName(isEnglish ? "Your Location" : "Lokasi Anda");
        }

        try {
          const prayerResponse = await apiClient.get("/prayer-times", {
            params: { latitude: location.latitude, longitude: location.longitude },
          });
          setPrayerTimes(prayerResponse.data);
          calculateNextPrayer(prayerResponse.data.timings);
        } catch (error) {
          console.warn("Could not fetch prayer times (this is normal if backend is not configured)");
        }
      } catch (error) {
        console.warn("Could not get location (this is normal if user denies permission)");
        setCityName(isEnglish ? "Your Location" : "Lokasi Anda");
      }
    };

    fetchLocationAndPrayerTimes();

    try {
      const [verseData, streakData, habitsData, logsData, lastReadData] = await Promise.all([
        getRandomAyah().catch(() => null),
        apiClient.get("/habits/streak").catch(() => ({ data: { streak: 0 } })),
        apiClient.get("/habits").catch(() => ({ data: [] })),
        apiClient.get(`/habits/logs/${getLocalDate()}`).catch(() => ({ data: [] })),
        apiClient.get("/last-read").catch(() => ({ data: null })),
      ]);

      const payload = {
        dailyVerse: verseData,
        streak: streakData.data.streak,
        habits: habitsData.data,
        todayLogs: logsData.data,
        lastRead: lastReadData.data,
      };
      applyHomeData(payload);
      homeCacheRef.current = { data: payload, fetchedAt: Date.now() };
    } catch (error) {
      console.error("Error loading home data:", error);
    } finally {
      homeLoadRef.current.inFlight = false;
      setLoading(false);
    }
  };

  const parseHHMM = (str) => {
    if (!str) return null;
    const [h, m] = str.slice(0, 5).split(":").map((x) => parseInt(x, 10));
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  };

  const calculateNextPrayer = (timings) => {
    const order = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
    const now = new Date();
    let found = null;
    for (const name of order) {
      const t = timings?.[name];
      const target = parseHHMM(t);
      if (!target) continue;
      if (target > now) {
        found = { name, time: t, target, isTomorrow: false };
        break;
      }
    }
    if (!found && timings?.Fajr) {
      const t = timings.Fajr;
      const target = parseHHMM(t);
      if (target) target.setDate(target.getDate() + 1);
      found = { name: "Fajr", time: t, target, isTomorrow: true };
    }
    setNextPrayer(found);
  };

  const formatDiff = (ms) => {
    if (ms <= 0) return isEnglish ? "Time to pray" : "Saatnya Shalat";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const hh = h > 0 ? String(h).padStart(2, "0") + ":" : "";
    return `${hh}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!nextPrayer?.target) return;
    const tick = () => {
      const now = new Date();
      const diff = nextPrayer.target - now;
      setCountdownText(formatDiff(diff));
      setIsTimeNow(diff <= 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextPrayer, isEnglish]);

  useEffect(() => {
    const pickQuote = () => {
      const selected = quotes[Math.floor(Math.random() * quotes.length)];
      return { text: isEnglish ? selected.en : selected.id, source: selected.source };
    };
    setQuote(pickQuote());
  }, [isEnglish]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (isEnglish) {
      if (hour < 12) return "Good Morning";
      if (hour < 15) return "Good Afternoon";
      if (hour < 18) return "Good Evening";
      return "Good Night";
    }
    if (hour < 12) return "Selamat Pagi";
    if (hour < 15) return "Selamat Siang";
    if (hour < 18) return "Selamat Sore";
    return "Selamat Malam";
  };

  const isHabitCompletedToday = (habitId) => {
    return todayLogs.some(log => log.habit_id === habitId);
  };
  const categoryColors = {
    quran: "#0D9488",
    shalat: "#7C3AED",
    infaq: "#F59E0B",
    intentions: "#22C55E",
    general: "#6B7280",
  };
  const renderPercentageLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    // Only show label if percentage is >= 10% to avoid clutter
    if (percent < 0.1) return null;
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 9, fontWeight: 600 }}>
        {`${Math.round(percent * 100)}%`}
      </text>
    );
  };

  const completedHabitIds = new Set(todayLogs.map((log) => log.habit_id));
  const completedHabits = completedHabitIds.size;
  const totalHabits = habits.length;
  const habitCompletion = totalHabits ? Math.round((completedHabits / totalHabits) * 100) : 0;

  const createShareCanvas = async (drawFn, fileName, shareText) => {
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    drawFn(ctx, canvas);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) return;
    const file = new File([blob], fileName, { type: "image/png" });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: shareText });
        return;
      } catch (error) {
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const drawDailyProgressCard = (ctx, canvas) => {
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, "#0D9488");
    gradient.addColorStop(1, "#1D4ED8");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.beginPath();
    ctx.arc(880, 200, 180, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 54px sans-serif";
    ctx.fillText(isEnglish ? "Daily Progress" : "Progress Harian", 80, 120);

    ctx.font = "400 28px sans-serif";
    const dateLabel = new Date(getLocalDate() + "T00:00:00").toLocaleDateString(
      isEnglish ? "en-US" : "id-ID",
      { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    );
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.fillText(dateLabel, 80, 165);

    ctx.font = "600 36px sans-serif";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(user?.name || (isEnglish ? "User" : "User"), 80, 235);

    const blocks = [
      { label: isEnglish ? "Streak" : "Streak", value: `${streak} ${isEnglish ? "days" : "hari"}` },
      { label: isEnglish ? "Habits" : "Kebiasaan", value: `${completedHabits}/${totalHabits}` },
      { label: isEnglish ? "Completion" : "Selesai", value: `${habitCompletion}%` }
    ];

    let x = 80;
    let y = 320;
    blocks.forEach((block) => {
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      ctx.fillRect(x, y, 280, 140);
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = "500 26px sans-serif";
      ctx.fillText(block.label, x + 20, y + 50);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "700 40px sans-serif";
      ctx.fillText(block.value, x + 20, y + 100);
      x += 320;
    });

    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.fillRect(80, 520, 920, 150);
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.font = "500 26px sans-serif";
    ctx.fillText(isEnglish ? "Last Read" : "Terakhir Dibaca", 110, 570);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "600 34px sans-serif";
    const lastReadText = lastRead
      ? `${lastRead.surah_name} • ${isEnglish ? "Ayah" : "Ayat"} ${lastRead.ayah_number}`
      : isEnglish
      ? "No recent reading"
      : "Belum ada bacaan";
    ctx.fillText(lastReadText, 110, 620);

    ctx.fillStyle = "rgba(255,255,255,0.8)";
    ctx.font = "500 22px sans-serif";
    ctx.fillText(isEnglish ? "QuranNext" : "QuranNext", 80, 1010);
  };

  const drawQuoteCard = (ctx, canvas) => {
    ctx.fillStyle = "#111827";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#F59E0B";
    ctx.fillRect(0, 0, canvas.width, 220);

    ctx.fillStyle = "#111827";
    ctx.font = "700 54px sans-serif";
    ctx.fillText(isEnglish ? "Daily Reminder" : "Pengingat Harian", 80, 140);

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "500 34px sans-serif";
    const lines = [];
    const words = (quote?.text || "").split(" ");
    let line = "";
    words.forEach((word) => {
      const testLine = `${line}${word} `;
      if (ctx.measureText(testLine).width > 900) {
        lines.push(line.trim());
        line = `${word} `;
      } else {
        line = testLine;
      }
    });
    if (line.trim()) lines.push(line.trim());
    let y = 360;
    lines.slice(0, 6).forEach((lineText) => {
      ctx.fillText(lineText, 80, y);
      y += 60;
    });

    ctx.fillStyle = "#FCD34D";
    ctx.font = "600 28px sans-serif";
    ctx.fillText(quote?.source || "", 80, 900);

    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.font = "500 22px sans-serif";
    ctx.fillText(isEnglish ? "Shared from QuranNext" : "Dibagikan dari QuranNext", 80, 1010);
  };

  const handleShareDailyProgress = async () => {
    setSharingProgress(true);
    await createShareCanvas(
      drawDailyProgressCard,
      "qurannext-daily-progress.png",
      isEnglish ? "My daily Quran progress" : "Progress Quran harian saya"
    );
    setSharingProgress(false);
  };

  const handleShareQuote = async () => {
    if (!quote) return;
    setSharingQuote(true);
    await createShareCanvas(
      drawQuoteCard,
      "qurannext-quote.png",
      `${quote.text} — ${quote.source}`
    );
    setSharingQuote(false);
  };
  const getHomeCategoryChartData = () => {
    if (!habits || habits.length === 0) return [];
    const agg = {};
    todayLogs.forEach(log => {
      const h = habits.find(h => h.habit_id === log.habit_id);
      const key = h?.category || "general";
      agg[key] = (agg[key] || 0) + 1;
    });
    return Object.entries(agg).map(([key, value]) => ({
      name: key === "quran" ? "Al-Qur'an" : key === "shalat" ? (isEnglish ? "Prayer" : "Shalat") : key === "infaq" ? "Infaq" : key === "intentions" ? (isEnglish ? "Intentions" : "Niat & Doa") : (isEnglish ? "Other" : "Lainnya"),
      value,
      key,
      color: categoryColors[key] || "#6B7280",
    })).filter(d => d.value > 0);
  };

  const shortcutMeta = {
    "/quran": { label: isEnglish ? "Quran" : "Quran", icon: BookOpen, color: "bg-primary" },
    "/hafalan": { label: isEnglish ? "Memorization" : "Hafalan", icon: Brain, color: "bg-primary" },
    "/habits": { label: isEnglish ? "Habits" : "Kebiasaan", icon: CheckCircle2, color: "bg-primary" },
    "/prayer-times": { label: isEnglish ? "Prayer Times" : "Jadwal", icon: Clock, color: "bg-primary" },
    "/doa": { label: isEnglish ? "Duas" : "Doa", icon: HandHeart, color: "bg-primary" },
    "/quiz": { label: "Quiz", icon: Target, color: "bg-primary" },
    "/bookmarks": { label: isEnglish ? "Bookmarks" : "Bookmark", icon: Bookmark, color: "bg-primary" },
    "/analytics": { label: isEnglish ? "Analytics" : "Analitik", icon: BarChart3, color: "bg-primary" },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/95 pb-20" data-testid="home-page">
      {/* Premium Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b">
        <div className="page-container py-2 sm:py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground mb-0.5">{isEnglish ? "Assalamu'alaikum" : "Assalamualaikum"}</p>
              <h1 className="text-lg sm:text-xl font-bold truncate">{getGreeting()}, {user?.name?.split(' ')[0] || (isEnglish ? "User" : "User")}</h1>
            </div>
            <Link to="/settings">
              <Avatar className="w-10 h-10 sm:w-12 sm:h-12 border-2 border-primary/20 hover:border-primary/40 transition-all shadow-lg hover:shadow-xl flex-shrink-0">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-base sm:text-lg font-bold">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            </Link>
          </div>
        </div>
      </div>

      <div className="page-container space-y-3 sm:space-y-4 pt-3 sm:pt-4">
        {/* Premium Daily Verse Card */}
        {dashboardPrefs?.showDailyVerse !== false && (
        <Card className="islamic-gradient text-white overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300" data-testid="daily-verse-card">
          <CardContent className="p-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <div className="flex-1 min-w-0">
                <span className="text-xs sm:text-sm font-semibold text-white/90 uppercase tracking-wide">{isEnglish ? "Verse of the Day" : "Ayat Hari Ini"}</span>
                {dailyVerse && (
                  <p className="text-white/70 text-xs sm:text-sm mt-0.5 truncate">
                    {dailyVerse.surah.englishName} : {dailyVerse.number}
                  </p>
                )}
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm flex-shrink-0 ml-2">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            </div>

            {/* Verse Content */}
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 sm:h-12 w-full bg-white/20 rounded" />
                <Skeleton className="h-4 sm:h-6 w-3/4 bg-white/20 rounded" />
                <Skeleton className="h-8 sm:h-10 w-24 sm:w-32 bg-white/20 rounded" />
              </div>
            ) : dailyVerse ? (
              <>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-5 mb-4">
                  <p className="font-arabic text-xl sm:text-2xl md:text-3xl text-right leading-loose text-white" dir="rtl">
                    {dailyVerse.arabic}
                  </p>
                </div>
                <p className="text-white/90 text-sm sm:text-base leading-relaxed mb-4">
                  {translation === "english" ? dailyVerse.english : dailyVerse.indonesian}
                </p>
                <Button
                  variant="secondary"
                  size="default"
                  className="w-full sm:w-auto bg-white/20 hover:bg-white/30 text-white border-0 shadow-lg text-sm sm:text-base h-9 sm:h-10 px-4 sm:px-6"
                  onClick={() => navigate(`/quran/${dailyVerse.surah.number}`)}
                >
                  {isEnglish ? "Read Full Verse" : "Baca Selengkapnya"} <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1 sm:ml-2" />
                </Button>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-white/70 text-sm">{isEnglish ? "Unable to load verse" : "Tidak dapat memuat ayat"}</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

      {/* Prayer Times Widget */}
      {dashboardPrefs?.showPrayerTimes !== false && (loading ? (
        <Card className="shadow-lg bg-gradient-to-br from-primary/10 via-primary/5 to-background overflow-hidden hover:shadow-xl transition-all duration-300">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Skeleton className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl" />
                <div className="space-y-1.5">
                  <Skeleton className="h-4 sm:h-5 w-20 sm:w-28" />
                  <Skeleton className="h-3 w-24 sm:w-36" />
                  <Skeleton className="h-2.5 w-20 sm:w-32" />
                </div>
              </div>
              <Skeleton className="h-8 sm:h-9 w-20 sm:w-24 rounded-lg text-xs" />
            </div>
            <Skeleton className="h-16 sm:h-20 w-full rounded-xl sm:rounded-2xl mb-3 sm:mb-4" />
            <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="p-2 sm:p-3 rounded-lg text-center">
                  <Skeleton className="h-4 w-4 mx-auto mb-1 sm:mb-1.5" />
                  <Skeleton className="h-2 w-8 mx-auto mb-1 sm:mb-1.5" />
                  <Skeleton className="h-3.5 w-10 mx-auto" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : prayerTimes ? (
        <Card className="shadow-lg bg-gradient-to-br from-primary/10 via-primary/5 to-background overflow-hidden hover:shadow-xl transition-all duration-300">
          <CardContent className="p-3 sm:p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20 flex-shrink-0">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base sm:text-lg font-bold truncate">{isEnglish ? "Prayer Times" : "Jadwal Shalat"}</h2>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                    <span className="truncate">{cityName || (isEnglish ? "Location detected" : "Lokasi terdeteksi")}</span>
                  </p>
                  {prayerTimes?.date?.hijri && prayerTimes?.date?.readable && (
                    <p className="text-[10px] sm:text-xs text-primary font-semibold mt-0.5 sm:mt-1 flex items-center gap-1">
                      📅 {prayerTimes.date.hijri.day} {hijriMonthId(prayerTimes.date.hijri.month)} {prayerTimes.date.hijri.year} H
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/prayer-times")}
                className="rounded-lg font-semibold h-8 sm:h-9 px-2 sm:px-3 text-xs hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex-shrink-0"
              >
                <span className="hidden sm:inline">{isEnglish ? "View All" : "Lihat Semua"}</span>
                <span className="sm:hidden">{isEnglish ? "All" : "Semua"}</span>
                <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-0.5 sm:ml-1" />
              </Button>
            </div>

            {/* Next Prayer Countdown - Premium */}
            {nextPrayer && (
              <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
                <div className="relative">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-md flex-shrink-0">
                        <Navigation className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs text-white/80 font-semibold mb-0.5">{isEnglish ? "Next Prayer" : "Shalat Berikutnya"}</p>
                        <p className="text-lg sm:text-2xl font-bold truncate">
                          {isEnglish ? prayerNames[nextPrayer.name]?.en || nextPrayer.name : prayerNames[nextPrayer.name]?.id || nextPrayer.name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-2xl sm:text-3xl md:text-4xl font-bold tracking-wide ${isTimeNow ? "text-yellow-300" : ""}`}>{countdownText || nextPrayer.time}</p>
                      <p className="text-[10px] sm:text-xs text-white/80 mt-0.5">
                        {isEnglish ? `At ${nextPrayer.time?.slice(0, 5)}` : `Jam ${nextPrayer.time?.slice(0, 5)}`}
                      </p>
                      {isTimeNow && (
                        <p className="text-[10px] sm:text-xs font-semibold text-yellow-300 mt-0.5">{isEnglish ? "It's time to pray" : "Saatnya Shalat"}</p>
                      )}
                      {nextPrayer.isTomorrow && (
                        <p className="text-[10px] sm:text-xs text-white/80 mt-0.5">{isEnglish ? "Tomorrow" : "Besok"}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Prayer Time Cards - Responsive */}
            <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
              {prayerTimes.timings && ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"].map((prayerName) => {
                const prayer = prayerNames[prayerName];
                const Icon = prayer.icon;
                const isNext = nextPrayer?.name === prayerName;

                return (
                  <div
                    key={prayerName}
                    className={`p-2 sm:p-3 rounded-xl text-center transition-all duration-300 ${
                      isNext
                        ? "bg-gradient-to-br from-primary/40 to-primary/20 border border-primary/50 shadow-md scale-[1.02]"
                        : "bg-gradient-to-br from-muted/50 to-muted/30 hover:from-muted hover:to-muted/50 hover:shadow-sm"
                    }`}
                  >
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mx-auto mb-1 sm:mb-1.5 ${isNext ? "text-primary" : "text-primary/70"}`} />
                    <p className={`text-[10px] sm:text-xs font-bold mb-1 uppercase tracking-wide ${isNext ? "text-primary" : "text-muted-foreground"}`}>
                      {isEnglish ? prayer.en : prayer.id}
                    </p>
                    <p className={`text-sm sm:text-base font-bold ${isNext ? "text-primary" : ""}`}>
                      {prayerTimes.timings[prayerName]?.slice(0, 5)}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null)}

      {/* Streak Card - Premium */}
      {dashboardPrefs?.showProgressSharing !== false && (
      <Card className="shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40 overflow-hidden" data-testid="streak-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20 flex-shrink-0">
              <Flame className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base sm:text-lg text-foreground">{isEnglish ? `${streak} Day Streak` : `${streak} Hari Streak`}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground font-medium">{isEnglish ? "Keep it up! Don't break the streak" : "Terus semangat! Jangan putus"}</p>
            </div>
          </div>

          {/* Weekly dots - Premium */}
          <div className="flex gap-2 sm:gap-3 justify-center bg-white/50 dark:bg-black/20 rounded-xl sm:rounded-2xl p-2 sm:p-3 backdrop-blur-sm">
            {(isEnglish ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['S', 'S', 'R', 'K', 'J', 'S', 'M']).map((day, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 sm:gap-2">
                <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground">{day}</span>
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${i < streak % 7 ? 'bg-gradient-to-br from-primary to-primary/80 text-white shadow-md shadow-primary/20' : 'bg-muted/50'}`}>
                  {i < streak % 7 && <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      <section>
        <div className="section-header mb-3">
          <h2 className="section-title text-base sm:text-lg font-bold">{isEnglish ? "Progress Sharing" : "Bagikan Progress"}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {dashboardPrefs?.showProgressSharing !== false && (
          <Card className="shadow-md hover:shadow-lg transition-all duration-300">
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm sm:text-base font-semibold">{isEnglish ? "Daily Progress Card" : "Kartu Progress Harian"}</h3>
                  <p className="text-xs text-muted-foreground">{isEnglish ? "Share your consistency today" : "Bagikan konsistensi harian"}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div className="rounded-xl border border-border p-3 sm:p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{isEnglish ? "Habits" : "Kebiasaan"}</span>
                  <span className="text-xs font-semibold">{completedHabits}/{totalHabits}</span>
                </div>
                <Progress value={habitCompletion} className="h-2" />
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{isEnglish ? "Streak" : "Streak"}</span>
                  <span className="font-semibold text-foreground">{streak} {isEnglish ? "days" : "hari"}</span>
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={handleShareDailyProgress}
                disabled={sharingProgress}
                className="w-full text-xs sm:text-sm h-9"
              >
                {sharingProgress ? (isEnglish ? "Preparing..." : "Menyiapkan...") : (isEnglish ? "Share Progress" : "Bagikan Progress")}
              </Button>
            </CardContent>
          </Card>
          )}

          {dashboardPrefs?.showQuote !== false && (
          <Card className="shadow-md hover:shadow-lg transition-all duration-300">
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm sm:text-base font-semibold">{isEnglish ? "Islamic Quote" : "Kutipan Islami"}</h3>
                  <p className="text-xs text-muted-foreground">{isEnglish ? "Generate & share reminders" : "Buat & bagikan pengingat"}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div className="rounded-xl border border-border p-3 sm:p-4 bg-muted/30 space-y-1.5">
                <p className="text-sm sm:text-base font-medium">{quote?.text || ""}</p>
                <p className="text-xs text-muted-foreground">{quote?.source || ""}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const selected = quotes[Math.floor(Math.random() * quotes.length)];
                    setQuote({ text: isEnglish ? selected.en : selected.id, source: selected.source });
                  }}
                  className="flex-1 text-xs sm:text-sm h-9"
                >
                  {isEnglish ? "Generate" : "Buat Kutipan"}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleShareQuote}
                  disabled={sharingQuote}
                  className="flex-1 text-xs sm:text-sm h-9"
                >
                  {sharingQuote ? (isEnglish ? "Preparing..." : "Menyiapkan...") : (isEnglish ? "Share" : "Bagikan")}
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
        </div>
      </section>

      {/* Quick Actions - Premium */}
      <section>
        <div className="section-header mb-3">
          <h2 className="section-title text-base sm:text-lg font-bold">{isEnglish ? "Quick Actions" : "Aksi Cepat"}</h2>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
          {(Array.isArray(favoriteShortcuts) ? favoriteShortcuts : ["/quran","/hafalan","/habits"]).filter((p) => shortcutMeta[p]).map((path) => {
            const meta = shortcutMeta[path];
            const IconComponent = meta.icon;
            return (
              <Link
                key={path}
                to={path}
                className="group relative overflow-hidden rounded-xl p-3 sm:p-4 bg-gradient-to-br from-card to-card/50 border border-border hover:shadow-lg hover:scale-[1.01] transition-all duration-300"
                data-testid={`quick-action-${meta.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${meta.color} text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300 flex-shrink-0`}>
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <span className="font-semibold text-xs sm:text-sm flex-1 truncate">{meta.label}</span>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-300 flex-shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Last Read - Premium */}
      {lastRead && (
        <section>
          <div className="section-header mb-3">
            <h2 className="section-title text-base sm:text-lg font-bold">{isEnglish ? "Last Read" : "Terakhir Dibaca"}</h2>
            <Link to="/quran" className="text-xs sm:text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
              {isEnglish ? "See all" : "Lihat semua"} <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline ml-0.5" />
            </Link>
          </div>
          <Card className="cursor-pointer hover:shadow-lg hover:scale-[1.005] transition-all duration-300 group" onClick={() => navigate(`/quran/${lastRead.surah_number}`)}>
            <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-md shadow-primary/20 group-hover:scale-105 transition-transform duration-300 flex-shrink-0">
                <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm sm:text-base mb-0.5 group-hover:text-primary transition-colors truncate">{lastRead.surah_name}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground font-medium">{isEnglish ? `Verse ${lastRead.ayah_number}` : `Ayat ${lastRead.ayah_number}`}</p>
              </div>
              <Button size="icon" variant="ghost" className="rounded-full h-9 w-9 sm:h-10 sm:w-10 hover:bg-primary/10 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 flex-shrink-0">
                <Play className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5" />
              </Button>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Habits Tracker - Premium */}
      {dashboardPrefs?.showHabits !== false && (
      <section>
        <div className="section-header mb-3">
          <div>
            <h2 className="section-title text-base sm:text-lg font-bold">{isEnglish ? "Today's Habits" : "Kebiasaan Hari Ini"}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isEnglish
                ? new Date(getLocalDate() + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                : new Date(getLocalDate() + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <Link to="/habits" className="text-xs sm:text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
            {isEnglish ? "Manage" : "Kelola"} <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline ml-0.5" />
          </Link>
        </div>

        {/* Category Chart Card */}
        <Card className="mb-3 sm:mb-4 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden">
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 items-center">
              <div className="h-48 sm:h-56 md:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getHomeCategoryChartData()}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={35}
                      outerRadius={70}
                      label={renderPercentageLabel}
                      labelLine={false}
                    >
                      {getHomeCategoryChartData().map((entry, index) => (
                        <Cell key={`home-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col justify-center gap-2 sm:gap-3">
                {getHomeCategoryChartData().length === 0 ? (
                  <div className="text-center py-4 sm:py-6">
                    <Circle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-muted-foreground/50" />
                    <p className="text-xs sm:text-sm text-muted-foreground">{isEnglish ? "No completed habits yet" : "Belum ada kebiasaan selesai"}</p>
                  </div>
                ) : (
                  (() => {
                    const list = getHomeCategoryChartData();
                    const total = list.reduce((sum, item) => sum + item.value, 0) || 1;
                    return list.map((d) => {
                      const pct = Math.round((d.value / total) * 100);
                      return (
                        <div key={d.key} className="flex items-center gap-1.5 sm:gap-2 bg-muted/30 rounded-md sm:rounded-lg p-1.5 sm:p-2">
                          <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full shadow-sm flex-shrink-0" style={{ backgroundColor: d.color }}></span>
                          <span className="text-xs sm:text-sm font-medium flex-1 truncate">{d.name}</span>
                          <span className="text-xs sm:text-sm font-bold flex-shrink-0">{d.value} <span className="text-muted-foreground font-normal">({pct}%)</span></span>
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Empty State */}
        {habits.length === 0 ? (
          <Card className="shadow-md">
            <CardContent className="p-5 sm:p-6 text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-muted/50 flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 sm:w-7 sm:h-7 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground mb-3 sm:mb-4 font-medium text-sm sm:text-base">{isEnglish ? "No habits yet" : "Belum ada kebiasaan"}</p>
              <Button
                variant="outline"
                size="default"
                onClick={() => navigate("/habits")}
                className="rounded-lg font-semibold h-9 sm:h-10 px-4 text-sm"
              >
                {isEnglish ? "Add Habit" : "Tambah Kebiasaan"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {habits.slice(0, 3).map((habit) => {
              const completed = isHabitCompletedToday(habit.habit_id);
              return (
                <Card
                  key={habit.habit_id}
                  className={`group hover:shadow-lg transition-all duration-300 ${
                    completed ? "border-green-500 border bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20" : ""
                  }`}
                >
                  <CardContent className="p-3 flex items-center gap-2.5 sm:gap-3">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-md transition-all duration-300 flex-shrink-0 ${
                        completed ? "bg-gradient-to-br from-green-500 to-emerald-600 scale-105" : ""
                      }`}
                      style={completed ? {} : { backgroundColor: `${habit.color}20` }}
                    >
                      {completed ? (
                        <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      ) : (
                        <Circle className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: habit.color }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold text-sm sm:text-base mb-0.5 ${completed ? "line-through text-green-700 dark:text-green-300" : ""} truncate`}>{habit.name}</h3>
                      <p className="text-xs text-muted-foreground font-medium">
                        {completed ? (isEnglish ? "Completed today" : "Selesai hari ini") : (isEnglish ? "Not completed" : "Belum selesai")}
                      </p>
                    </div>
                    {completed && (
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-green-500 flex items-center justify-center shadow-md flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
      )}
      </div>
    </div>
  );
};

export default Home;
